import React, { useEffect, useState } from 'react';
import categoryContent from '../../utils/categoryContent';

type HomepageFields = {
  title: string;
  description: string;
  h1: string;
  intro: string;
};

const emptyPage = (): HomepageFields => categoryContent.resolveHomePage(null);

const HomepageIntroEditor: React.FC = () => {
  const [form, setForm] = useState<HomepageFields>(emptyPage);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/homepage')
      .then((response) => (response.ok ? response.json() : null))
      .then((page) => {
        if (!cancelled && page) setForm(categoryContent.resolveHomePage(page));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (field: keyof HomepageFields, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setMessage('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await fetch('/api/homepage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setError('The homepage could not be saved.');
        return;
      }
      setForm(categoryContent.resolveHomePage(result.page));
      setMessage('Saved. The homepage now shows this introduction.');
    } catch (saveError) {
      setError('The homepage could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content-management">
      <div className="content-management-header">
        <h2>Homepage introduction</h2>
        <p>The title bar and the text directly under it. The same layout as a gallery category page.</p>
      </div>
      <div className="form-group">
        <label htmlFor="homepage-title">Search title</label>
        <input
          id="homepage-title"
          className="form-input"
          value={form.title}
          onChange={(event) => update('title', event.target.value)}
        />
        <small className={form.title.length > 60 ? 'category-count-warn' : ''}>
          {form.title.length} characters. Search titles work best at 60 or fewer.
        </small>
      </div>
      <div className="form-group">
        <label htmlFor="homepage-description">Meta description</label>
        <textarea
          id="homepage-description"
          className="form-textarea"
          rows={3}
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
        />
        <small className={form.description.length > 155 ? 'category-count-warn' : ''}>
          {form.description.length} characters. Search descriptions work best at 155 or fewer.
        </small>
      </div>
      <div className="form-group">
        <label htmlFor="homepage-heading">Page heading</label>
        <input
          id="homepage-heading"
          className="form-input"
          value={form.h1}
          onChange={(event) => update('h1', event.target.value)}
        />
      </div>
      <div className="form-group">
        <label htmlFor="homepage-intro">Intro</label>
        <textarea
          id="homepage-intro"
          className="form-textarea"
          rows={8}
          value={form.intro}
          onChange={(event) => update('intro', event.target.value)}
        />
        <small>Shown under the heading. A blank line starts a new paragraph.</small>
      </div>
      <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save homepage'}
      </button>
      {message ? <p className="content-form-note">{message}</p> : null}
      {error ? <p className="content-form-note">{error}</p> : null}
    </div>
  );
};

export default HomepageIntroEditor;
